import { cn } from '@/lib/utils';
import { useMapStore } from '@/stores/mapStore';
import { useCollaborationStore } from '@/stores/collaborationStore';
import type { ConnectionStatus } from '@/types/collaboration';

interface StatusBarProps {
  connectionStatus: ConnectionStatus;
}

export function StatusBar({ connectionStatus }: StatusBarProps) {
  const { cursorCoordinates, viewport, selection } = useMapStore();
  const { remoteUsers } = useCollaborationStore();

  const formatCoord = (value: number, isLat: boolean): string => {
    const dir = isLat ? (value >= 0 ? 'N' : 'S') : (value >= 0 ? 'E' : 'W');
    return `${Math.abs(value).toFixed(5)}${dir}`;
  };

  const statusLabel = {
    connected: 'Connected',
    connecting: 'Connecting...',
    disconnected: 'Disconnected',
    error: 'Error',
  }[connectionStatus] || 'Unknown';

  return (
    <footer className="h-6 flex items-center px-3 text-[11px] bg-card border-t border-border text-muted-foreground font-mono">
      {/* Connection */}
      <div className="flex items-center gap-1.5 pr-3 border-r border-border">
        <div className={cn('status-dot', connectionStatus)} />
        <span>{statusLabel}</span>
      </div>

      {/* Users */}
      <div className="px-3 border-r border-border">
        {remoteUsers.length + 1} online
      </div>

      {/* Coordinates */}
      <div className="px-3 border-r border-border">
        {cursorCoordinates ? (
          <span>
            {formatCoord(cursorCoordinates.lat, true)}, {formatCoord(cursorCoordinates.lng, false)}
          </span>
        ) : (
          <span className="text-muted-foreground/50">--, --</span>
        )}
      </div>

      {/* Zoom */}
      <div className="px-3 border-r border-border">
        Z {viewport.zoom.toFixed(1)}
      </div>

      {/* Selection */}
      {selection.featureIds.length > 0 && (
        <div className="px-3 text-primary">
          {selection.featureIds.length} selected
        </div>
      )}

      <div className="flex-1" />

      <a
        href="https://github.com/danielyj147/overwatch"
        target="_blank"
        rel="noopener noreferrer"
        className="text-muted-foreground/50 hover:text-muted-foreground transition-colors mr-3"
      >
        GitHub
      </a>
      <a
        href="https://www.linkedin.com/in/danielyj147/"
        target="_blank"
        rel="noopener noreferrer"
        className="text-muted-foreground/50 hover:text-muted-foreground transition-colors mr-3"
      >
        Built by Daniel Jeong
      </a>
      <span className="text-muted-foreground/50">v1.0.0</span>
    </footer>
  );
}
